import {observable, computed, action, makeObservable} from 'mobx';
import moment from 'moment';
import Cookie from 'js-cookie';
import * as Sentry from '@sentry/react';

import {COOKIE_PREFERENCE_NAME, COOKIE_PREFERENCE_MAX_AGE, PRIVACY_POLICY_VERSION} from '../constants';
import {COOKIE_PREFERENCE_OPTIONS} from '../enums';

const {YES, NO} = COOKIE_PREFERENCE_OPTIONS;

const isValidPreferenceOption = preferenceOption => [NO, YES].includes(preferenceOption);

const writePreferencesCookie = value => {
  const expires = moment().add(COOKIE_PREFERENCE_MAX_AGE, 'ms').toDate();
  Cookie.set(COOKIE_PREFERENCE_NAME, value, {
    expires
  });
};

class CookiePreference {
  // State
  appName = undefined;

  @observable cookiePreference = NO;
  @observable cookiePreferenceDialogOption = NO;
  @observable bannerIsDismissed = false;
  @observable showCookiePreferenceDialog = false;
  @observable savePreferenceButtonLoading = false;
  @observable acceptAllCookiesButtonLoading = false;
  @observable translations = {};

  @action
  setAcceptAllCookiesButtonLoading = acceptAllCookiesButtonLoading =>
    (this.acceptAllCookiesButtonLoading = acceptAllCookiesButtonLoading);

  @action
  setSavePreferenceButtonLoading = savePreferenceButtonLoading =>
    (this.savePreferenceButtonLoading = savePreferenceButtonLoading);

  @computed
  get currentCookieValue() {
    return Cookie.get(COOKIE_PREFERENCE_NAME);
  }

  @computed
  get showBanner() {
    const preferenceCookieValue = this.currentCookieValue;
    const cookieHasValidValue = [NO, PRIVACY_POLICY_VERSION].includes(preferenceCookieValue);
    return !cookieHasValidValue && !this.bannerIsDismissed;
  }

  // Public
  constructor({appName, enablePosthog, disablePosthog}) {
    this.appName = appName;
    this.enablePosthog = enablePosthog.bind(this);
    this.disablePostHog = disablePosthog.bind(this);
    makeObservable(this);
  }

  setNotification = notification => {
    this.notification = notification;
  };

  @action
  setTranslations = translations => {
    this.translations = translations;
  };

  updateCookiesFromCurrentPreferenceCookie = async () => {
    if (this.currentCookieValue !== PRIVACY_POLICY_VERSION) {
      await this.disablePostHog(this.appName);
    }
  };

  @action
  acceptAllCookies = async consentCookiesMutation => {
    this.setCookiePreferenceOption(YES);
    await this.save(consentCookiesMutation, this.setAcceptAllCookiesButtonLoading);
  };

  @action
  setCookiePreferenceOption = cookiePreference => {
    if (isValidPreferenceOption(cookiePreference)) {
      this.cookiePreferenceDialogOption = cookiePreference;
      return true;
    } else {
      return false;
    }
  };

  @action
  savePreferences = async consentCookiesMutation => {
    await this.save(consentCookiesMutation, this.setSavePreferenceButtonLoading);
  };

  @action
  openCookiePreferencesDialog = () => {
    this.loadCookieData();
    this.showCookiePreferenceDialog = true;
  };

  @action
  hideCookiePreferencesDialog = () => {
    this.showCookiePreferenceDialog = false;
  };

  @action
  loadCookieData = () => {
    this.cookiePreference = Cookie.get(COOKIE_PREFERENCE_NAME) === PRIVACY_POLICY_VERSION ? YES : NO;
    this.cookiePreferenceDialogOption = this.cookiePreference;
    this.bannerIsDismissed = false;
  };

  // Private

  @action
  hideBanner = () => {
    this.bannerIsDismissed = true;
  };

  @action
  save = async (consentCookiesMutation, setLoading) => {
    setLoading(true);

    if (this.cookiePreferenceDialogOption === YES) {
      try {
        const {data} = await consentCookiesMutation({version: PRIVACY_POLICY_VERSION});

        if (data.consentCookies.result === true) {
          this.cookiePreference = YES;
          writePreferencesCookie(PRIVACY_POLICY_VERSION);
          await this.enablePosthog(this.appName);

          this.hideBanner();
          this.hideCookiePreferencesDialog();
        } else {
          throw new Error('Could not save preferences');
        }
      } catch (error) {
        Sentry.captureException(error, {extra: {version: PRIVACY_POLICY_VERSION}});
        this.notification.error(this.translations.couldntSavePreferences);
      }
    } else {
      this.cookiePreference = NO;
      writePreferencesCookie(NO);
      await this.disablePostHog(this.appName);
      this.hideBanner();
      this.hideCookiePreferencesDialog();
    }

    setLoading(false);
  };
}

export default CookiePreference;
